import { Anchor, csx } from '@vtex/admin-ui'
import React, { Suspense, useMemo } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { FormattedMessage } from 'react-intl'

import { useDrawer } from '../../../../ui/components'
import { PaymentsMethodsListDrawer } from './PaymentsMethodsListDrawer'
import { useProductPageUrlParams } from '../../hooks'
import type { ProductSkuPaymentData_Product$key } from './__generated__/ProductSkuPaymentData_Product.graphql'
import { BestPaymentMethod } from './BestPaymentMethod'

const productFragment = graphql`
  fragment ProductSkuPaymentData_Product on Product {
    skus {
      edges {
        node {
          id
          paymentOptions(addresses: $addresses, paymentMethod: $paymentMethod) {
            edges {
              node {
                __typename
              }
            }
            ...BestPaymentMethod_PaymentOptionConnection
            ...PaymentsMethodsListDrawer_PaymentOptionConnection
          }
        }
      }
    }
  }
`

export type ProductSkuPaymentDataContentProps = {
  productRef: ProductSkuPaymentData_Product$key
}

const ProductSkuPaymentDataContent = ({
  productRef,
}: ProductSkuPaymentDataContentProps) => {
  const { selectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productFragment, productRef)

  const paymentsMethodsDrawer = useDrawer({
    route: '#/payments-methods-drawer',
  })

  const selectedSku = useMemo(() => {
    return product.skus.edges.find(
      (skuEdge) => skuEdge.node.id === selectedSkuId
    )
  }, [product.skus.edges, selectedSkuId])

  const handleShowPaymentsMethodsTextClick = () => {
    paymentsMethodsDrawer.open()
  }

  const handlePaymentsMethodsDrawerClose = () => {
    paymentsMethodsDrawer.close()
  }

  if (!selectedSku?.node.paymentOptions?.edges.length) {
    return null
  }

  return (
    <div className={csx({ marginTop: '$space-1' })}>
      <BestPaymentMethod
        paymentOptionConnectionRef={selectedSku.node.paymentOptions}
      />
      <div>
        <Anchor
          onClick={handleShowPaymentsMethodsTextClick}
          className={csx({
            text: '$action2',
          })}
        >
          <FormattedMessage id="pdp.payment.show-more" />
        </Anchor>
      </div>
      <PaymentsMethodsListDrawer
        isOpen={paymentsMethodsDrawer.isOpen}
        onClose={handlePaymentsMethodsDrawerClose}
        paymentOptionConnectionRef={selectedSku.node.paymentOptions}
      />
    </div>
  )
}

export type ProductSkuPaymentDataProps = {
  productRef: ProductSkuPaymentData_Product$key
}

export const ProductSkuPaymentData = ({
  productRef,
}: ProductSkuPaymentDataProps) => (
  <Suspense fallback={null}>
    <ProductSkuPaymentDataContent productRef={productRef} />
  </Suspense>
)
